// Make room for clinical audio when the browser refuses a write for quota.
//
// The LucasAI database holds two very different kinds of data: the durable
// recording queue (audio that exists NOWHERE else until it uploads) and the
// offline caches (patients/chats/offlineMeta — a copy of server state that can be
// refetched at any time). When the origin is out of quota, one of them has to give,
// and it is never the audio. This module drops cache rows only; it never opens the
// `recordings` store, which is the point rather than an implementation detail —
// same posture as offlineCache.clearOfflineCaches, whose comment already states it:
// "caches die, the recording queue does NOT".
//
// Two tiers, because not every cache row is equally safe to drop:
//   FOREIGN scopes — another user+org's cached rows on a shared clinic workstation.
//     Always safe: nobody in this session is reading them, and their owner refetches
//     on their next sign-in. (Dropping them is a small privacy win too.)
//   The CURRENT scope — only when the caller says the app is online, because
//     offline mode is exactly what that copy exists for. Taking it from a clinician
//     who is offline to make room for audio would trade one loss for another.
import { promisifyRequest, awaitTransaction } from "./lucasDb";

const CACHE_STORES = ["patients", "chats"];

// A quota refusal, across engines: Chrome/Firefox throw DOMException
// "QuotaExceededError"; older WebKit uses the legacy code 22; Safari has also been
// seen reporting the generic name with the quota text in the message.
export function isQuotaError(error) {
  if (!error) return false;
  const name = error.name || "";
  if (name === "QuotaExceededError" || name === "NS_ERROR_DOM_QUOTA_REACHED") return true;
  if (error.code === 22 || error.code === 1014) return true;
  return /quota/i.test(error.message || "");
}

// Delete every cache row this pass is allowed to drop. Returns the number of rows
// removed — 0 means there was nothing safe to free, and the caller should not
// bother retrying its write. Never throws: reclaiming is best-effort, and a failure
// here must not replace the caller's original (more informative) error.
export async function reclaimCacheSpace(db, { currentScope, includeCurrentScope = false } = {}) {
  if (!db) return 0;
  let deleted = 0;
  try {
    const tx = db.transaction([...CACHE_STORES, "offlineMeta"], "readwrite");
    const dropScope = (scope) => includeCurrentScope || !currentScope || scope !== currentScope;

    // Cursor rather than getAll: the rows are the thing we are short of memory and
    // space for, so they are never all held at once.
    const deleteMatchingRows = (store) =>
      new Promise((resolve, reject) => {
        let removed = 0;
        const req = store.openCursor();
        req.onsuccess = (event) => {
          const cursor = event.target.result;
          if (!cursor) {
            resolve(removed);
            return;
          }
          if (dropScope(cursor.value?.scope)) {
            cursor.delete();
            removed++;
          }
          cursor.continue();
        };
        req.onerror = (event) => reject(event.target?.error || event);
      });

    for (const storeName of CACHE_STORES) {
      deleted += await deleteMatchingRows(tx.objectStore(storeName));
    }

    // The freshness stamps for whatever was just dropped. Keyed `${store}|${scope}`
    // (offlineCache.replaceScopeRows); anything not in that shape is left alone.
    const meta = tx.objectStore("offlineMeta");
    const metaKeys = await promisifyRequest(meta.getAllKeys());
    for (const key of metaKeys || []) {
      if (typeof key !== "string") continue;
      const sep = key.indexOf("|");
      if (sep < 0) continue;
      if (!CACHE_STORES.includes(key.slice(0, sep))) continue;
      if (dropScope(key.slice(sep + 1))) {
        meta.delete(key);
        deleted++;
      }
    }

    await awaitTransaction(tx);
    return deleted;
  } catch (e) {
    console.warn("Storage reclaim failed", e?.name || e?.message || e);
    return 0;
  }
}
